import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import DeleteProjectModal from "../../../components/DeleteProjectModal";
import { setCurrentProject, setProjects } from "../../../redux/project/projectSlice";
import { projectsService } from "../../../services/projects";

const Management = () => {
  const dispatch = useDispatch();
  const project = useSelector((state) => state.project.currentProject);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const [unarchiving, setUnarchiving] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [exportError, setExportError] = useState(null);
  const [exporting, setExporting] = useState("");

  const refreshProjects = (onDone) => {
    projectsService.listProjects({ page_size: 500 }, (response) => {
      const results = Array.isArray(response.data)
        ? response.data
        : response.data?.results ?? [];
      dispatch(setProjects(results));
      onDone?.(results);
    });
  };

  const handleArchive = () => {
    if (!window.confirm(
      `Archive ${project.name}? Your live site will go offline and the GitHub repo will be archived. Your data stays in Weave.`
    )) {
      return;
    }
    setArchiving(true);
    setActionError(null);
    projectsService.archiveProject(
      project.hash,
      (response) => {
        const updated = response.data;
        dispatch(setCurrentProject(updated));
        refreshProjects(() => {
          setArchiving(false);
        });
      },
      (error) => {
        setActionError(error?.response?.data?.error || "Could not archive site");
        setArchiving(false);
      }
    );
  };

  const handleUnarchive = () => {
    if (!window.confirm(
      `Unarchive ${project.name}? We'll restore the GitHub repo and redeploy the site.`
    )) {
      return;
    }
    setUnarchiving(true);
    setActionError(null);
    projectsService.unarchiveProject(
      project.hash,
      (response) => {
        const updated = response.data;
        dispatch(setCurrentProject(updated));
        refreshProjects(() => {
          setUnarchiving(false);
        });
      },
      (error) => {
        setActionError(error?.response?.data?.error || "Could not unarchive site");
        setUnarchiving(false);
      }
    );
  };

  const handleDelete = (confirmation) => {
    setDeleting(true);
    setDeleteError(null);
    projectsService.deleteProject(
      project.hash,
      confirmation,
      () => {
        window.location = "/";
      },
      (error) => {
        setDeleteError(error?.response?.data?.error || "Could not delete project");
        setDeleting(false);
      }
    );
  };

  const canExport = Boolean(project?.purchased_site);

  const download = async (format) => {
    if (!canExport || !project?.hash) return;
    setExportError(null);
    setExporting(format);
    const apiBase =
      process.env.REACT_APP_API_URL
      || (process.env.NODE_ENV === "production"
        ? "https://a.weave.md"
        : "http://localhost:8000");
    try {
      const response = await fetch(
        `${apiBase}/api/v1/projects/${project.hash}/export/?kind=${format}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` } },
      );
      if (!response.ok) {
        let message = "Could not download export.";
        try {
          const data = await response.json();
          message = data.error || message;
        } catch {
          // non-JSON error body
        }
        setExportError(message);
        return;
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${project.slug}.${format}`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      setExportError("Could not download export.");
    } finally {
      setExporting("");
    }
  };

  const isArchived = project?.is_archived || project?.status === "archived";

  return (
    <div className="max-w-2xl space-y-8">
      {isArchived && (
        <div className="rounded-xl border border-theme-secondary bg-theme-secondary/30 p-4">
          <p className="text-sm font-semibold">This site is archived</p>
          <p className="mt-1 text-xs text-theme-text opacity-70">
            Your project data is still here. The live site and preview are no longer available.
            Unarchive below to bring the site back, or delete permanently if you are done with it.
          </p>
        </div>
      )}

      <section>
        <h2 className="mb-4 text-sm font-semibold">Export</h2>
        {canExport ? (
          <p className="mb-4 text-xs opacity-60">
            Download a snapshot of your site tables, or request a codebase package.
          </p>
        ) : (
          <p className="mb-4 text-xs opacity-60">
            Export is available with an I'll take it from here purchase linked to this site.{" "}
            <Link to="/account/billing/#handoff" className="text-theme-primary hover:opacity-80">
              View handoff options
            </Link>
          </p>
        )}
        {exportError && (
          <div className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-500">
            {exportError}
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          {["csv", "json"].map((format) => (
            <button
              key={format}
              type="button"
              onClick={() => download(format)}
              disabled={!canExport || Boolean(exporting)}
              className="rounded-lg border border-theme-secondary px-4 py-2 text-sm font-semibold disabled:opacity-50"
            >
              {exporting === format ? "Downloading…" : `Download ${format.toUpperCase()}`}
            </button>
          ))}
          <button
            type="button"
            onClick={() => download("codebase")}
            disabled={!canExport || Boolean(exporting)}
            className="rounded-lg border border-theme-secondary px-4 py-2 text-sm font-semibold disabled:opacity-50"
          >
            {exporting === "codebase" ? "Requesting…" : "Download codebase"}
          </button>
        </div>
      </section>

      <section>
        <h2 className="mb-4 text-sm font-semibold">Gift site</h2>
        <p className="mb-4 text-xs opacity-60">
          Send this site to someone and invite them to Weave.
        </p>
        <Link
          to="/invites/"
          className="inline-block rounded-lg border border-theme-secondary px-4 py-2 text-sm font-semibold"
        >
          Gift site
        </Link>
      </section>

      <section className="rounded-xl border border-red-500/40 bg-red-500/10 p-5">
        <h2 className="mb-4 text-sm font-semibold text-red-500">Danger zone</h2>
        {!isArchived ? (
          <>
            <p className="mb-4 text-xs text-theme-text opacity-60">
              Archive takes your site offline and archives the GitHub repo, but keeps your project and data.
              Delete is only available after a site is archived.
            </p>
            {actionError && (
              <div className="mb-4 rounded border border-red-500 bg-red-500/10 p-3 text-sm text-theme-text">
                {actionError}
              </div>
            )}
            <button
              type="button"
              onClick={handleArchive}
              disabled={archiving}
              className="rounded-lg border border-red-500/40 px-4 py-2 text-sm font-semibold text-red-500 disabled:opacity-50"
            >
              {archiving ? "Archiving..." : "Archive site"}
            </button>
          </>
        ) : (
          <>
            <p className="mb-4 text-xs text-theme-text opacity-60">
              Unarchive restores the GitHub repo and redeploys hosting. Delete permanently removes
              Cloudflare hosting, the GitHub repo, and all project data.
            </p>
            {actionError && (
              <div className="mb-4 rounded border border-red-500 bg-red-500/10 p-3 text-sm text-theme-text">
                {actionError}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleUnarchive}
                disabled={unarchiving}
                className="rounded-lg border border-red-500/40 px-4 py-2 text-sm font-semibold text-red-500 disabled:opacity-50"
              >
                {unarchiving ? "Restoring..." : "Unarchive site"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setDeleteError(null);
                  setShowDeleteModal(true);
                }}
                className="rounded-lg bg-red-500 px-4 py-2 text-sm font-semibold text-white"
              >
                Delete forever
              </button>
            </div>
          </>
        )}
      </section>

      <DeleteProjectModal
        project={project}
        open={showDeleteModal}
        onClose={() => {
          if (!deleting) {
            setShowDeleteModal(false);
            setDeleteError(null);
          }
        }}
        onConfirm={handleDelete}
        deleting={deleting}
        error={deleteError}
      />
    </div>
  );
};

export default Management;
